import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useMountEffect } from "primereact/hooks";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { useIntl } from "react-intl";
import {
  fullProfileRequest,
  updateDetailRequest,
  userDetailsRequest,
} from "../../../store/slices/selfSlice";
import DetailsCard from "../../common/detailsCard";
import { Skeleton } from "primereact/skeleton";
import { Message } from "primereact/message";
import AvatarWithProgress from "../../common/avatarWithProgress";
import * as Displayhelper from "../../../helpers/DisplayHelper";
import { GlobalConstants } from "../../../constants";

import "./profile.scss";

const Profile = ({
  record,
  fullProfileRequest,
  updateDetailRequest,
  userDetailsRequest,
  error,
  action,
  requesting,
  enterprise,
}) => {
  const intl = useIntl();
  const [profileCompletion, setProfileCompletion] = useState(0);

  useMountEffect(() => {
    fullProfileRequest();
  });

  useEffect(() => {
    if (!error && !requesting && action === "update-profile") {
      userDetailsRequest();
      fullProfileRequest();
    }
  }, [error, action, requesting]);

  const handleOnSubmit = (section, data) => {
    if (section !== "custom") {
      updateDetailRequest({
        id: record?.id,
        detailType: section,
        ...data,
      });
    } else {
      updateDetailRequest({
        id: record?.id,
        detailType: "dynamic_details",
        details: data,
      });
    }
  };

  const calculateProfileCompletion = useCallback(() => {
    let totalFields = 4 + 4;
    record?.sections?.forEach((section) => {
      totalFields += section.fields?.length;
    });
    let completedFields = 4;
    if (record?.designation) completedFields++;
    if (record?.department) completedFields++;
    if (record?.managerId) completedFields++;
    if (record?.phoneNumber) completedFields++;

    record?.sections?.forEach((section) => {
      completedFields += section.fields?.filter((field) => field.value).length;
    });
    setProfileCompletion(Math.floor((completedFields / totalFields) * 100));
  }, [record]);

  // Helper function to map numeric field types to string types
  const getFieldTypeString = useCallback((numericType) => {
    const fieldType = GlobalConstants.FIELD_TYPES.find(
      (type) => type.value === numericType
    );
    return fieldType ? fieldType.name : "text";
  }, []);

  useEffect(() => {
    if (record) {
      calculateProfileCompletion();
    }
  }, [record, calculateProfileCompletion]);

  const { learnerSettings = {} } = enterprise?.learningConfiguration || {};
  const { canUpdateSelfProfile } = learnerSettings;

  return (
    <div
      className="edexlms-profile"
      style={{
        height: "calc(100vh - 64px - 20px - 20px)",
        overflowY: "auto",
        padding: "10px",
      }}
    >
      {(record && (
        <div className="edexlms-user-banner">
          <div className="edexlms-user-basic-details flex justify-content-between align-items-center">
            <div className="edexlms-user-image flex flex-column gap-2 justify-content-center align-items-center">
              <AvatarWithProgress
                label={Displayhelper.getUserShortName(
                  record?.name ||
                    `${record?.firstName}${
                      record?.lastName ? ` ${record?.lastName}` : ""
                    }`
                )}
                completion={profileCompletion}
                radius={54}
              />
              <div className="edexlms-profile-completion">
                <>
                  {intl.formatMessage(
                    { id: "profile_completion" },
                    {
                      percentage: profileCompletion,
                      b: (chunks) => (
                        <span className="text-lg font-bold">{chunks}</span>
                      ),
                    }
                  )}
                </>
              </div>
            </div>
            <div className="edexlms-user-details flex flex-column gap-2">
              <div className="flex justify-content-center align-items-center">
                <div className="edexlms-user-name font-bold text-xl">
                  {record?.name ||
                    `${record?.firstName}${
                      record?.lastName ? ` ${record?.lastName}` : ""
                    }`}
                </div>
                <Message
                  severity={record?.isActive ? "success" : "error"}
                  text={intl.formatMessage({
                    id: record?.isActive ? "active" : "inactive",
                  })}
                  style={{ marginLeft: "10px", padding: "7px" }}
                />
              </div>
              <div className="flex justify-content-center align-items-center gap-3">
                <i className="pi pi-briefcase text-muted"></i>
                <div className="edexlms-user-email text-muted">
                  {record?.designation || "-"}
                </div>
              </div>
              <div className="flex justify-content-center align-items-center gap-3">
                <i className="pi pi-envelope text-muted"></i>
                <div className="edexlms-user-email text-muted">
                  {record?.email || "-"}
                </div>
              </div>
              <div className="flex justify-content-center align-items-center gap-3">
                <i className="pi pi-mobile text-muted"></i>
                <div className="edexlms-user-email text-muted">
                  {record?.phoneNumber || "-"}
                </div>
              </div>
            </div>
          </div>
          <div className="edexlms-user-batches flex-grow-1"></div>
        </div>
      )) || <Skeleton width="100%" height="169px"></Skeleton>}
      <div className="edexlms-tab-content">
        <ResponsiveMasonry
          style={{ paddingRight: "10px" }}
          columnsCountBreakPoints={{ 350: 1, 900: 2 }}
          gutterBreakpoints={{ 350: "12px", 900: "16px" }}
        >
          <Masonry>
            <DetailsCard
              canUpdate={canUpdateSelfProfile}
              message={intl.formatMessage({
                id: "personal_details_message",
              })}
              onSubmit={(data) => handleOnSubmit("personal_details", data)}
              style={{ flex: 1 }}
              details={record}
              fields={[
                {
                  label: "firstName",
                  value: record?.firstName,
                  isRequired: true,
                  type: "text",
                  i18n: true,
                },
                {
                  label: "lastName",
                  value: record?.lastName,
                  isRequired: true,
                  type: "text",
                  i18n: true,
                },
                {
                  label: "email",
                  value: record?.email,
                  isRequired: true,
                  type: "text",
                  i18n: true,
                  readOnly: true,
                },
                {
                  label: "username",
                  value: record?.username,
                  isRequired: true,
                  type: "text",
                  i18n: true,
                  readOnly: true,
                },
              ]}
              title={intl.formatMessage({ id: "personal_details" })}
            />
            {record?.sections
              ?.filter((section) => section.fields.length)
              .map((section) => (
                <DetailsCard
                  key={section.name}
                  onSubmit={(data) => handleOnSubmit("custom", data)}
                  canUpdate={canUpdateSelfProfile}
                  style={{ flex: 1 }}
                  details={record}
                  title={section.name}
                  fields={section.fields.map((field) => ({
                    label: field.label,
                    name: field.id,
                    value: field.value,
                    isRequired: field.isRequired,
                    type: field.type,
                    maxLength: field.maxLength,
                    placeholder: field.placeholder,
                    options: field.options?.map((option) => ({
                      name: option,
                      id: option,
                      value: option,
                    })),
                  }))}
                />
              ))}
          </Masonry>
        </ResponsiveMasonry>
      </div>
    </div>
  );
};

export const mapStateToProps = (state) => ({
  record: state?.self?.fullProfile || {},
  action: state?.self?.action,
  requesting: state?.self?.requesting,
  error: state?.global?.error,
  enterprise: state?.global?.enterprise,
});

export const mapDispatchToProps = (dispatch) => ({
  fullProfileRequest: () => dispatch(fullProfileRequest()),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  userDetailsRequest: () => dispatch(userDetailsRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
